import {
    AfterContentInit,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { SampleBoardMaterialRecipeItem } from "@app/strain/sample-boards/models/sample-board-material-recipe-item.model";
import {
    SampleBoardConstructionInstructions
} from "@app/strain/sample-boards/models/documentation/sample-board-construction-instructions.model";

/**
 * The sample board recipe component is used in order to leverage the represenation of a sample board recipe in the system.
 *
 * It now only renders a static recipe view with the provided materials list.
 */
@Component({
    selector: 'app-sample-board-recipe',
    templateUrl: './sample-board-recipe.component.html',
    styleUrls: ['./sample-board-recipe.component.scss']
})
export class SampleBoardRecipeComponent implements OnChanges, OnInit, AfterContentInit {
    readonly i18nKey = 'admin.strain.sample-boards.details.tabs.materials.recipe';

    @Input() headerTitle?: string;
    @Input() headerSubtitle?: string;
    @Input() headerDescription?: string;
    @Input() headerLeftIcon = 'fa-microchip';
    @Input() headerBackground: 'default' | 'green' | 'yellow' | 'red' | 'disabled' | 'disabled-done' | 'none' = 'default';
    @Input() headerText: 'default' | 'green-text' | 'yellow-text' | 'red-text' = 'default';
    @Input() headerPlaceholderTitle?: string;
    @Input() headerPlaceholderSubtitle?: string;
    @Input() items: SampleBoardRecipeSourceItem[] = [];
    @Input() isLoading = false;
    @Input() loadingLabel?: string;
    @Input() constructionInstructions: SampleBoardConstructionInstructions;
    @Input() isSavingConstructionInstructions = false;
    @Input() isDownloadingConstructionInstructions = false;
    @Input() canUploadConstruction = true;

    @Output() uploadConstructionInstructions = new EventEmitter<File>();
    @Output() downloadConstructionInstructions = new EventEmitter<SampleBoardConstructionInstructions>();
    @Output() openMaterial = new EventEmitter<SampleBoardMaterialRecipeItem>();

    @ViewChild('constructionFileInput') constructionFileInput: ElementRef<HTMLInputElement>;

    @ContentChildren('[recipeFooterActions]', { descendants: true }) footerActions: QueryList<any>;

    internalItems: SampleBoardMaterialRecipeItem[] = [];
    hasFooterActions = false;

    constructor(private translate: TranslateService) {
    }

    ngOnInit(): void {
        this.initializeItems();
    }

    ngAfterContentInit(): void {
        this.hasFooterActions = this.footerActions && this.footerActions.length > 0;
        this.footerActions?.changes?.subscribe((actions: QueryList<any>) => this.hasFooterActions = actions.length > 0);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['items']) {
            this.initializeItems();
        }
    }

    get resolvedPlaceholderTitle(): string {
        return this.headerPlaceholderTitle || this.translate.instant(`${this.i18nKey}.header.placeholderTitle`);
    }

    get resolvedPlaceholderSubtitle(): string {
        return this.headerPlaceholderSubtitle || this.translate.instant(`${this.i18nKey}.header.placeholderSubtitle`);
    }

    get footerTitle(): string {
        return this.translate.instant(`${this.i18nKey}.footer.overview`);
    }

    get footerSubtitle(): string {
        const count = (this.internalItems || []).length;
        return this.translate.instant(`${this.i18nKey}.footer.materialLines`, { count });
    }

    get hasConstructionInstructions(): boolean {
        return !!this.constructionInstructions;
    }

    private initializeItems(): void {
        const base = (this.items && this.items.length ? this.items : [])
            .map(item => this.normalizeItem(item));

        this.internalItems = base.length ? base : [];
    }

    private normalizeItem(item: SampleBoardRecipeSourceItem): SampleBoardMaterialRecipeItem {
        return item instanceof SampleBoardMaterialRecipeItem
            ? item
            : new SampleBoardMaterialRecipeItem(item);
    }

    onUploadClick(): void {
        if (!this.canUploadConstruction) {
            return;
        }
        this.constructionFileInput?.nativeElement?.click();
    }

    onFileSelected(event: Event): void {
        const target = event.target as HTMLInputElement;
        const file = target?.files && target.files.length ? target.files[0] : null;
        if (file) {
            this.uploadConstructionInstructions.emit(file);
        }
        if (target) {
            target.value = '';
        }
    }

    onDownload(): void {
        if (!this.constructionInstructions) {
            return;
        }
        this.downloadConstructionInstructions.emit(this.constructionInstructions);
    }

    onOpenMaterial(item: SampleBoardMaterialRecipeItem): void {
        this.openMaterial.emit(item);
    }
}

export type SampleBoardRecipeSourceItem =
    SampleBoardMaterialRecipeItem;
